import gql from 'graphql-tag';
import { DocumentNode } from 'graphql';

const driverFragment: DocumentNode = gql`
  fragment driverFragment on Driver {
    id
    name
    imageUrl
  }
`;

const problemItemRequirements: DocumentNode = gql`
  fragment problemItemRequirements on FacilityProblemRequirement {
    image
    description
    report
    document
    timeSelector
    reason
    markAsUnprocessed
  }
`;

const problemItem: DocumentNode = gql`
  fragment problemItem on FacilityProblem {
    id
    name
    bagProblem
    itemProblem
    shortCode
    shownInServices
    requirements {
      ...problemItemRequirements
    }
    readOnly
    reasons
  }
`;

const problemItemFragments = gql`
  ${problemItem}
  ${problemItemRequirements}
`;

const facilityOrderProblemFull: DocumentNode = gql`
  fragment facilityOrderProblemFull on FacilityOrderProblem {
    id
    name
    qty
    affectedType
    processingTime
    description
    resolveType
    reportType
    confirmedAt
    unprocessed
    resolvedAt
    reportedAt
    customerInstruction
    reason
    suggestedServiceLabel
    bag {
      qrCode
      service
      serviceShortCode
      totalBags
      totalItems
      totalHangers
      status
    }
    problem {
      ...problemItem
    }
    item {
      name
      qty
      suggestion
      special
      cost
      photos {
        photo
        id
      }
    }
    photos {
      photo
      id
    }
    reportedByName
  }
`;

const facilityOrderProblemFullFragments = gql`
  ${facilityOrderProblemFull}
  ${problemItemFragments}
`;

const serviceCountryReturnData: DocumentNode = gql`
  fragment serviceCountryReturnData on ServiceCountryType {
    weightUnit
    currency
    phonePrefix
    stationPriceLength
    code
    timezone
  }
`;

const notificationsData: DocumentNode = gql`
  fragment notificationsData on FacilityOrderNotification {
    type
    message
    expire
  }
`;

const facilityOrderInTimeData: DocumentNode = gql`
  fragment facilityOrderInTimeData on FacilityOrderInTime {
    label
    readyBy
    shortLabel
    orders {
      id
      number
      customer
      status
      deliveryStatus
      bagCount
      batchCount
      hangerCount
      collectedBags
      servicesShortCodes
      lastDropoffId
      submittedForRiskAssessment
      prioritised
      firstTime
      facilityOrderBags {
        connectedTags
        id
        qrCode
        service
        serviceShortCode
        status
      }
      driverObject {
        ...driverFragment
      }
    }
  }
`;

const facilityOrderInTimeDataFragments = gql`
  ${facilityOrderInTimeData}
  ${driverFragment}
`;

const orderData: DocumentNode = gql`
  fragment orderData on Order {
    uuid
    firstTime
    number
    readyBy
    readyAt
    readyByTime
    readyByLabel
    timezone
    customerName
    facilityNote
    submittedForRiskAssessment
    orderStatus {
      name
    }
  }
`;

const facilityOrderItem = gql`
  fragment facilityOrderItem on FacilityOrderItem {
    id
    qty
    reprocessing
    weight
    service
    serviceShortCode
    unit
    name
    cost
    description
    special
    suggestion
    unprocessed
    priceGroupPhoto
    reason
    problems {
      id
      resolvedAt
      reportType
      reportedAt
      confirmedAt
      resolveType
      unprocessed
      qty
      description
      processingTime
      suggestedServiceShortCode
      reason
      photos {
        id
        photo
      }
      problem {
        ...problemItem
      }
    }
    photos {
      id
      photo
      photoType
    }
  }
`;

const facilityOrderItemFragments = gql`
  ${facilityOrderItem}
  ${problemItemFragments}
`;

const itemsGroupedByService = gql`
  fragment itemsGroupedByService on FacilityOrderItemGroupedByService {
    serviceLabel
    serviceShortCode
    items {
      ...facilityOrderItem
    }
  }
`;

const itemsGroupedByServiceFragments = gql`
  ${itemsGroupedByService}
  ${facilityOrderItemFragments}
`;

const facilityOrderReturnData = gql`
  fragment facilityOrderReturnData on FacilityOrder {
    id
    originalFacility
    collectedBags
    packCustomerBags
    status
    deliveryStatus
    comments
    notifications {
      ...notificationsData
    }
    order {
      ...orderData
    }
    itemsGroupedByService {
      ...itemsGroupedByService
    }
    alreadyCollectedByDriver
    driver {
      ...driverFragment
    }
    facilityOrderPackages {
      id
      qty
      connectedBagId
      packageType
      qrCode
      serviceShortCode
      deliveredAt
    }
    facilityOrderBags {
      id
      qrCode
      comments
      connectedTags {
        id
        qrCode
        serviceShortCode
      }
      status
      serviceShortCode
      totalBags
      totalItems
      totalWeight
      totalBatches
      totalHangers
      items {
        ...facilityOrderItem
      }
      itemsGroupedByService {
        ...itemsGroupedByService
      }
      notifications {
        ...notificationsData
      }
    }
    actions {
      editingPackagesAllowed
    }
    packingPopupRequired
    packingPopup {
      title
      text
    }
  }
`;

const facilityOrderReturnDataFragments = gql`
  ${facilityOrderReturnData}
  ${notificationsData}
  ${orderData}
  ${driverFragment}
  ${itemsGroupedByServiceFragments}
  ${facilityOrderItemFragments}
`;

const bagProblemShared = gql`
  fragment bagProblemShared on FacilityOrderProblem {
    id
    name
    description
    qty
    reportedAt
    reportType
    resolvedAt
    resolveType
    confirmedAt
    affectedType
    processingTime
    unprocessed
    problem {
      ...problemItem
    }
    item {
      id
      name
      service
      serviceShortCode
    }
    bag {
      qrCode
      service
    }
  }
`;

const bagProblemSharedFragments = gql`
  ${bagProblemShared}
  ${problemItemFragments}
`;

const bagResolvedProblem = gql`
  fragment bagResolvedProblem on FacilityOrderProblem {
    ...bagProblemShared
  }
`;

const bagResolvedProblemFragments = gql`
  ${bagResolvedProblem}
  ${bagProblemSharedFragments}
`;

const bagUnresolvedProblem = gql`
  fragment bagUnresolvedProblem on FacilityOrderProblem {
    ...bagProblemShared
    bag {
      connectedTags {
        id
        qrCode
      }
      service
      serviceShortCode
      qrCode
      facilityOrder {
        order {
          readyBy
          readyByTime
          readyByLabel
          timezone
        }
        itemsGroupedByService {
          ...itemsGroupedByService
        }
        facilityOrderPackages {
          id
          qty
          connectedBagId
          packageType
          qrCode
          serviceShortCode
          deliveredAt
        }
      }
    }
  }
`;

const bagUnresolvedProblemFragments = gql`
  ${bagUnresolvedProblem}
  ${bagProblemSharedFragments}
  ${itemsGroupedByServiceFragments}
`;

// This one is without facilityOrder so we do not loop the data
const fullFacilityOrderBagReturnData = gql`
  fragment fullFacilityOrderBagReturnData on FacilityOrderBag {
    id
    status
    qrCode
    comments
    connectedTags {
      id
      qrCode
      serviceShortCode
    }
    service
    serviceShortCode
    totalBags
    totalItems
    totalWeight
    totalBatches
    totalHangers
    items {
      ...facilityOrderItem
    }
    itemsGroupedByService {
      ...itemsGroupedByService
    }
    allowPacking
    packingPictureRequired
  }
`;

const fullFacilityOrderBagReturnDataFragments = gql`
  ${fullFacilityOrderBagReturnData}
  ${facilityOrderItemFragments}
  ${itemsGroupedByServiceFragments}
`;

// This one extends fullFacilityOrderBagReturnData with facilityOrder
const facilityOrderBagReturnData = gql`
  fragment facilityOrderBagReturnData on FacilityOrderBag {
    ...fullFacilityOrderBagReturnData
    reprocessingInstructions
    availableProcessingHours
    resolvedProblems {
      ...bagResolvedProblem
    }
    unresolvedProblems {
      ...bagUnresolvedProblem
    }
    notifications {
      ...notificationsData
    }
    facilityOrder {
      ...facilityOrderReturnData
    }
  }
`;

const facilityOrderBagReturnDataFragments = gql`
  ${facilityOrderBagReturnData}
  ${fullFacilityOrderBagReturnDataFragments}
  ${bagResolvedProblemFragments}
  ${bagUnresolvedProblemFragments}
  ${notificationsData}
  ${facilityOrderReturnDataFragments}
`;

const facility = gql`
  fragment facility on Facility {
    id
    name
    shortCode
    tomorrowOrderPrediction
    tomorrowOrderPredictionRange
    serviceCountry {
      ...serviceCountryReturnData
    }
    user {
      firstName
      lastName
    }
  }
`;

const facilityFragments = gql`
  ${facility}
  ${serviceCountryReturnData}
`;

const fullFacilityOrder = gql`
  fragment fullFacilityOrder on FacilityOrder {
    id
    status
    deliveryStatus
    comments
    packCustomerBags
    originalFacility
    notifications {
      ...notificationsData
    }
    order {
      ...orderData
    }
    resolvedProblems {
      ...facilityOrderProblemFull
    }
    unresolvedProblems {
      ...facilityOrderProblemFull
    }
    confirmedProblems {
      ...facilityOrderProblemFull
    }
    unconfirmedProblems {
      ...facilityOrderProblemFull
    }
    facilityOrderPackages {
      id
      qty
      connectedBagId
      packageType
      qrCode
      serviceShortCode
      deliveredAt
    }
    facilityOrderBags {
      ...fullFacilityOrderBagReturnData
    }
    itemsGroupedByService {
      serviceLabel
      serviceShortCode
      items {
        id
        name
        qty
        special
        weight
        unit
        unprocessed
        problems {
          id
          resolvedAt
          reportType
          reportedAt
          confirmedAt
          unprocessed
          resolveType
          qty
          description
          processingTime
          suggestedServiceShortCode
          reason
          photos {
            id
            photo
          }
          problem {
            ...problemItem
          }
        }
      }
    }
    actions {
      editingPackagesAllowed
    }
    totalBags
    totalItems
    totalWeight
    totalBatches
    totalHangers
    collectedBags
    alreadyCollectedByDriver
    prioritised
    driver {
      ...driverFragment
    }
    packingPopupRequired
    packingPopup {
      title
      text
    }
  }
`;

const fullFacilityOrderFragments = gql`
  ${fullFacilityOrder}
  ${notificationsData}
  ${orderData}
  ${facilityOrderProblemFullFragments}
  ${fullFacilityOrderBagReturnDataFragments}
  ${driverFragment}
`;

export const signUpFacilityWorkerSchema: DocumentNode = gql`
  mutation signUpFacilityWorker(
    $facilityId: Int!
    $name: String!
    $email: String
    $phone: String
  ) {
    signUpFacilityWorker(
      input: {
        facilityId: $facilityId
        name: $name
        email: $email
        phone: $phone
      }
    ) {
      id
      name
      email
      phone
      locale
      facility {
        ...facility
      }
    }
  }
  ${facilityFragments}
`;

export const signInFacilityWorkerSchema: DocumentNode = gql`
  mutation signInFacilityWorker($token: String!) {
    signInFacilityWorker(input: { token: $token }) {
      facilityWorker {
        id
        name
        email
        phone
        locale
        facility {
          ...facility
        }
      }
      token
    }
  }
  ${facilityFragments}
`;

export const getFacilityOrdersSchema: DocumentNode = gql`
  query facilityOrders {
    facilityOrders(mergeReprocessingOrders: true) {
      today {
        ...facilityOrderInTimeData
      }
      tomorrow {
        ...facilityOrderInTimeData
      }
      future {
        ...facilityOrderInTimeData
      }
      past {
        ...facilityOrderInTimeData
      }
    }
  }
  ${facilityOrderInTimeDataFragments}
`;

export const getFacilityOrdersFuturePastSchema: DocumentNode = gql`
  query facilityOrders {
    facilityOrders(mergeReprocessingOrders: true) {
      future {
        ...facilityOrderInTimeData
      }
      past {
        ...facilityOrderInTimeData
      }
    }
  }
  ${facilityOrderInTimeDataFragments}
`;

export const getFacilityOrdersTomorrowSchema: DocumentNode = gql`
  query facilityOrders {
    facilityOrders(mergeReprocessingOrders: true) {
      tomorrow {
        ...facilityOrderInTimeData
      }
    }
  }
  ${facilityOrderInTimeDataFragments}
`;

export const getFacilityOrdersTodaySchema: DocumentNode = gql`
  query facilityOrders {
    facilityOrders(mergeReprocessingOrders: true) {
      today {
        ...facilityOrderInTimeData
      }
    }
  }
  ${facilityOrderInTimeDataFragments}
`;

export const getFacilityServiceFilterSchema: DocumentNode = gql`
  query facilityServiceFilter {
    facilityServiceFilter {
      code
      label
    }
  }
`;

export const getFacilityPastOrdersSchema: DocumentNode = gql`
  query facilityPastOrders(
    $page: Int!
    $perPage: Int!
    $filter: FacilityOrderFilter
  ) {
    facilityPastOrders(page: $page, perPage: $perPage, filter: $filter) {
      pagination {
        size
        totalPages
        currentPage
        previousPage
        nextPage
      }
      orders {
        id
        number
        customer
        collectedBags
        status
        deliveryStatus
        lastDropoffId
        completedAtDate
        facilityOrderBags {
          service
          serviceShortCode
        }
      }
    }
  }
`;

export const facilityWorkerQRScan: DocumentNode = gql`
  query facilityWorkerQRScan($qrCode: String!) {
    facilityWorkerQRScan(qrCode: $qrCode) {
      id
      name
      user {
        firstName
        lastName
      }
      serviceCountry {
        code
        name
      }
    }
  }
`;

export const facilityOrder: DocumentNode = gql`
  query facilityOrder($id: ID!) {
    facilityOrder(id: $id) {
      ...fullFacilityOrder
    }
  }
  ${fullFacilityOrderFragments}
`;

export const facilityOrderBag: DocumentNode = gql`
  query facilityOrderBag($id: ID!) {
    facilityOrderBag(id: $id) {
      ...facilityOrderBagReturnData
    }
  }
  ${facilityOrderBagReturnDataFragments}
`;

export const facilityServices: DocumentNode = gql`
  query facilityServices($bagId: ID!) {
    facilityServices(bagId: $bagId) {
      id
      name
      selected
      serviceShortCode
      showCategories
      minimumServiceHours
      withoutPriceList
      popularItems {
        id
        name
        wash
      }
      categories {
        id
        name
        photo
        items {
          id
          name
          wash
          selected
          unit
          priceGroupId
          priceGroupPhoto
          priceServiceId
        }
      }
    }
  }
`;

export const facilityOrderBagAddItem: DocumentNode = gql`
  mutation facilityOrderBagAddItem(
    $bagId: ID!
    $facilityItemId: ID!
    $unprocessed: Boolean!
    $qty: Int
    $weight: Float
  ) {
    facilityOrderBagAddItem(
      input: {
        id: $bagId
        facilityItemId: $facilityItemId
        qty: $qty
        weight: $weight
        unprocessed: $unprocessed
      }
    ) {
      createdFacilityOrderItemId
      ...facilityOrderBagReturnData
    }
  }
  ${facilityOrderBagReturnDataFragments}
`;

export const facilityOrderBagFinishItemisation: DocumentNode = gql`
  mutation facilityOrderBagFinishItemisation(
    $bagId: ID!
    $processingTime: Int
  ) {
    facilityOrderBagFinishItemisation(
      input: { id: $bagId, processingTime: $processingTime }
    ) {
      ...facilityOrderBagReturnData
    }
  }
  ${facilityOrderBagReturnDataFragments}
`;

export const facilityOrderBagEditItem: DocumentNode = gql`
  mutation facilityOrderBagEditItem(
    $bagItemId: ID!
    $unprocessed: Boolean!
    $qty: Int
    $weight: Float
  ) {
    facilityOrderBagEditItem(
      input: {
        id: $bagItemId
        qty: $qty
        weight: $weight
        unprocessed: $unprocessed
      }
    ) {
      ...facilityOrderBagReturnData
    }
  }
  ${facilityOrderBagReturnDataFragments}
`;

export const facilityOrderBagRemoveItem: DocumentNode = gql`
  mutation facilityOrderBagRemoveItem($bagItemId: ID!) {
    facilityOrderBagRemoveItem(input: { id: $bagItemId }) {
      ...facilityOrderBagReturnData
    }
  }
  ${facilityOrderBagReturnDataFragments}
`;

export const facilityOrderBagAddSpecialItem: DocumentNode = gql`
  mutation facilityOrderBagAddSpecialItem(
    $bagId: ID!
    $serviceId: Int!
    $name: String!
    $description: String
    $suggestion: String
    $cost: Float
    $photos: [String]
    $reprocessing: Boolean
    $facilityItemId: ID
    $unprocessed: Boolean
    $qty: Int
    $weight: Float
    $processingTime: Int
    $reason: FacilityProblemSpecialItemReasonEnumType
  ) {
    facilityOrderBagAddSpecialItem(
      input: {
        id: $bagId
        priceServiceId: $serviceId
        name: $name
        description: $description
        suggestion: $suggestion
        cost: $cost
        photos: $photos
        reprocessing: $reprocessing
        facilityItemId: $facilityItemId
        unprocessed: $unprocessed
        qty: $qty
        weight: $weight
        processingTime: $processingTime
        reason: $reason
      }
    ) {
      createdFacilityOrderItemId
      ...facilityOrderBagReturnData
    }
  }
  ${facilityOrderBagReturnDataFragments}
`;

export const facilityOrderBagEditSpecialItem: DocumentNode = gql`
  mutation facilityOrderBagEditSpecialItem(
    $specialItemId: ID!
    $name: String
    $description: String
    $cost: Float
    $unprocessed: Boolean
    $suggestion: String
    $qty: Int
    $weight: Float
    $processingTime: Int
    $reason: FacilityProblemSpecialItemReasonEnumType
  ) {
    facilityOrderBagEditSpecialItem(
      input: {
        id: $specialItemId
        name: $name
        description: $description
        suggestion: $suggestion
        cost: $cost
        unprocessed: $unprocessed
        qty: $qty
        weight: $weight
        processingTime: $processingTime
        reason: $reason
      }
    ) {
      ...facilityOrderBagReturnData
    }
  }
  ${facilityOrderBagReturnDataFragments}
`;

export const facilityOrderEditItemPhoto: DocumentNode = gql`
  mutation facilityOrderEditItemPhoto($imageId: ID!, $photo: String!) {
    facilityOrderEditItemPhoto(input: { id: $imageId, photo: $photo }) {
      id
      photo
      photoType
    }
  }
`;

export const facilityOrderAddItemPhoto: DocumentNode = gql`
  mutation facilityOrderAddItemPhoto(
    $facilityOrderItemId: ID!
    $photo: String!
    $photoType: PhotoTypeEnumType
  ) {
    facilityOrderAddItemPhoto(
      input: { id: $facilityOrderItemId, photo: $photo, photoType: $photoType }
    ) {
      id
      photo
      photoType
    }
  }
`;

export const facilityOrderAddPackages: DocumentNode = gql`
  mutation facilityOrderAddPackages(
    $facilityOrderId: ID!
    $bagIds: [Int]
    $packages: [BagPackageInputType!]!
  ) {
    facilityOrderAddPackages(
      input: { id: $facilityOrderId, bagIds: $bagIds, packages: $packages }
    ) {
      ...fullFacilityOrder
    }
  }
  ${fullFacilityOrderFragments}
`;

export const facilityOrderPackBags: DocumentNode = gql`
  mutation facilityOrderPackBags($facilityOrderId: ID!, $bagIds: [ID!]!) {
    facilityOrderPackBags(input: { id: $facilityOrderId, bagIds: $bagIds }) {
      ...fullFacilityOrder
    }
  }
  ${fullFacilityOrderFragments}
`;

export const facilityOrderEditPackages: DocumentNode = gql`
  mutation facilityOrderEditPackages(
    $facilityOrderId: ID!
    $bagIds: [Int]
    $packages: [BagPackageInputType!]!
  ) {
    facilityOrderEditPackages(
      input: { id: $facilityOrderId, bagIds: $bagIds, packages: $packages }
    ) {
      ...fullFacilityOrder
    }
  }
  ${fullFacilityOrderFragments}
`;

export const facilityOrderFinishPacking: DocumentNode = gql`
  mutation facilityOrderFinishPacking($facilityOrderId: ID!) {
    facilityOrderFinishPacking(input: { id: $facilityOrderId }) {
      ...fullFacilityOrder
    }
  }
  ${fullFacilityOrderFragments}
`;

export const getFacilityOrderItem: DocumentNode = gql`
  query facilityOrderItem($facilityOrderItemId: ID!) {
    facilityOrderItem(id: $facilityOrderItemId) {
      ...facilityOrderItem
    }
  }
  ${facilityOrderItemFragments}
`;

/*************************************
 * PROBLEM REPORTING APIS
 *************************************/

export const facilityProblemReport: DocumentNode = gql`
  mutation facilityProblemReport(
    $bagId: ID!
    $problemTypeId: ID!
    $facilityOrderItemId: ID
    $reportType: ProblemReportTypeEnumType!
    $description: String
    $suggestedServiceShortCode: String
    $qty: Int
    $processingTime: Int
    $photos: [String]
    $reason: FacilityProblemReasonEnumType
    $unprocessed: Boolean
  ) {
    facilityProblemReport(
      input: {
        id: $bagId
        facilityProblemId: $problemTypeId
        facilityOrderItemId: $facilityOrderItemId
        description: $description
        reportType: $reportType
        suggestedServiceShortCode: $suggestedServiceShortCode
        qty: $qty
        photos: $photos
        processingTime: $processingTime
        reason: $reason
        unprocessed: $unprocessed
      }
    ) {
      id
      suggestedServiceShortCode
      resolvedAt
      resolveType
      affectedType
      reportType
      unprocessed
      problem {
        ...problemItem
      }
    }
  }
  ${problemItemFragments}
`;

export const facilityEditProblem: DocumentNode = gql`
  mutation facilityEditProblem(
    $problemId: ID!
    $description: String
    $suggestedServiceShortCode: String
    $qty: Int
    $processingTime: Int
    $reason: FacilityProblemReasonEnumType
    $unprocessed: Boolean
  ) {
    facilityEditProblem(
      input: {
        id: $problemId
        description: $description
        suggestedServiceShortCode: $suggestedServiceShortCode
        qty: $qty
        processingTime: $processingTime
        reason: $reason
        unprocessed: $unprocessed
      }
    ) {
      id
      suggestedServiceShortCode
      resolvedAt
      resolveType
      affectedType
      reportType
      unprocessed
      problem {
        ...problemItem
      }
    }
  }
  ${problemItemFragments}
`;

export const facilityServiceMismatchList: DocumentNode = gql`
  query facilityServiceMismatchList($bagId: ID!) {
    facilityServiceMismatchList(id: $bagId) {
      facilityProblemId
      serviceMismatchList {
        selected
        name
        shortCode
      }
    }
  }
`;

export const facilityProblemsList: DocumentNode = gql`
  query facilityProblemsList {
    facilityProblemsList {
      ...problemItem
    }
  }
  ${problemItemFragments}
`;

export const facilityDeleteProblem: DocumentNode = gql`
  mutation facilityDeleteProblem($problemId: ID!) {
    facilityDeleteProblem(input: { id: $problemId }) {
      success
      message
    }
  }
`;

export const facilityOrderEditProblemPhoto: DocumentNode = gql`
  mutation facilityOrderEditProblemPhoto($imageId: ID!, $photo: String!) {
    facilityOrderEditProblemPhoto(input: { id: $imageId, photo: $photo }) {
      id
      photo
    }
  }
`;

export const facilityOrderRemoveProblemPhoto: DocumentNode = gql`
  mutation facilityOrderRemoveProblemPhoto($imageId: ID!) {
    facilityOrderRemoveProblemPhoto(input: { id: $imageId }) {
      success
      message
    }
  }
`;

export const facilityOrderAddProblemPhoto: DocumentNode = gql`
  mutation facilityOrderAddProblemPhoto($problemId: ID!, $photo: String!) {
    facilityOrderAddProblemPhoto(input: { id: $problemId, photo: $photo }) {
      id
      photo
    }
  }
`;

export const facilityOrderBagConnectTag: DocumentNode = gql`
  mutation facilityOrderBagConnectTag(
    $bagId: ID!
    $qrCode: String!
    $serviceShortCode: String
  ) {
    facilityOrderBagConnectTag(
      input: {
        id: $bagId
        qrCode: $qrCode
        serviceShortCode: $serviceShortCode
      }
    ) {
      ...facilityOrderBagReturnData
    }
  }
  ${facilityOrderBagReturnDataFragments}
`;

export const facilityOrderBagRemoveTag: DocumentNode = gql`
  mutation facilityOrderBagRemoveTag($tagId: ID!) {
    facilityOrderBagRemoveTag(input: { id: $tagId }) {
      ...facilityOrderBagReturnData
    }
  }
  ${facilityOrderBagReturnDataFragments}
`;

export const facilityOrderBagRemoveTags: DocumentNode = gql`
  mutation facilityOrderBagRemoveTags($tagIds: [ID!]!) {
    facilityOrderBagRemoveTags(input: { ids: $tagIds }) {
      ...facilityOrderBagReturnData
    }
  }
  ${facilityOrderBagReturnDataFragments}
`;

export const supportCenterOrderList: DocumentNode = gql`
  query ordersWithProblems {
    ordersWithProblems {
      received {
        id
        orderNumber
        customerName
        readyBy
        reprocessing
        itemProblemCount
        bagProblemCount
        problemStatus
      }
      waiting {
        id
        orderNumber
        customerName
        readyBy
        reprocessing
        itemProblemCount
        bagProblemCount
        problemStatus
      }
      resolved {
        id
        orderNumber
        customerName
        readyBy
        reprocessing
        itemProblemCount
        bagProblemCount
        problemStatus
      }
    }
  }
`;

// Used for count only
export const receivedOrdersWithProblems: DocumentNode = gql`
  query ordersWithProblems {
    ordersWithProblems {
      received {
        id
      }
    }
  }
`;

export const countries: DocumentNode = gql`
  query countries {
    countries {
      name
      isoCode
      dialCode
      flag
    }
  }
`;

export const facilityConfirmProblem: DocumentNode = gql`
  mutation facilityConfirmProblem($facilityOrderProblemId: ID!) {
    facilityConfirmProblem(input: { id: $facilityOrderProblemId }) {
      ...facilityOrderProblemFull
    }
  }
  ${facilityOrderProblemFullFragments}
`;

export const getFacility: DocumentNode = gql`
  query getFacility {
    facility {
      ...facility
    }
  }
  ${facilityFragments}
`;

export const surveyAnswered: DocumentNode = gql`
  query surveyAnswered($id: ID!) {
    surveyAnswered(id: $id)
  }
`;
